/* eslint-disable react-hooks/exhaustive-deps */
import React, { useRef, useState, useEffect } from 'react'
import { toast } from 'react-toastify'
import { useSelector } from 'react-redux'
import { useTranslation } from 'react-i18next'
import { useForm, Controller } from 'react-hook-form'
import ProductsModal from '~/components/Sponsor/Modals/ProductsModal'
import { InputTitle } from '~/components/Shared/StyledComponents'

import ErrorMessage from '~/components/Shared/ErrorMessage'

import { StartDateSection, EndDateSection } from '~/components/Shared/CreateFormatDateSection'
import useSponsorFunctions from '~/helper/sponsorFunctions'
import slidingTopBanner from '~/schemaValidation/slidingTopBanner'
import { uploadMediaToS3WithId } from '~/services/requests'
import { transformDateToUtcFormat } from '~/helper/getFormattedDates'
import SponsoredBannerPreview from '../SponsoredBannerPreview'

import * as S from './styled'

const ModalEditSponsoredBanner = props => {
  const { t } = useTranslation()
  const { updateCampaignFormatAction } = useSponsorFunctions()
  const [loading, setLoading] = useState(false)
  const currentCampaignForReviewPage = useSelector(state => state.createCampaign?.currentCampaignForReviewPage)
  const { handleSubmit, control, errors } = useForm({
    validationSchema: slidingTopBanner,
  })

  const {
    showModal,
    handleCloseModal,
    formatToUpdate,
    createMessage,
    refuseCampaignProduct,
    approveCampaignProduct,
    review,
    sponsor,
    formatId,
  } = props || {}

  const billboardImageInput = useRef(null)
  const compactImageInput = useRef(null)
  const mediumImageInput = useRef(null)

  const [loadingUpload, setLoadingUpload] = useState({
    billboard: false,
    compact: false,
    medium: false,
  })

  // Billboard variant states
  const [billboardMedia, setBillboardMedia] = useState(null)
  const [billboardUploadId, setBillboardUploadId] = useState(null)

  // Compact rectangle variant states
  const [compactMedia, setCompactMedia] = useState(null)
  const [compactUploadId, setCompactUploadId] = useState(null)

  // Medium rectangle variant states
  const [mediumMedia, setMediumMedia] = useState(null)
  const [mediumUploadId, setMediumUploadId] = useState(null)

  const [date, setDate] = useState(transformDateToUtcFormat(formatToUpdate?.date))
  const [endDate, setEndDate] = useState(transformDateToUtcFormat(formatToUpdate?.endDate))

  const resetForm = () => {
    setBillboardMedia(null)
    setBillboardUploadId(null)
    setCompactMedia(null)
    setCompactUploadId(null)
    setMediumMedia(null)
    setMediumUploadId(null)
    setLoadingUpload({
      billboard: false,
      compact: false,
      medium: false,
    })
    setDate('')
    setEndDate('')
  }

  const closeModal = () => {
    resetForm()
    handleCloseModal()
  }

  const refuseTheCampaignAndSendMessageToChat = () => {
    createMessage()
    refuseCampaignProduct()
    handleCloseModal()
  }

  const handleImageInputClick = event => {
    event.target.value = ''
  }

  const onSubmit = payload => {
    if (!billboardMedia && !compactMedia && !mediumMedia) {
      return toast.error(t('bannerValidation'))
    }

    const variants = []

    if (billboardMedia && billboardUploadId) {
      variants.push({
        type: 'billboard',
        uploadId: billboardUploadId,
        imageUrl: billboardMedia,
      })
    }

    if (compactMedia && compactUploadId) {
      variants.push({
        type: 'compact_rectangle',
        uploadId: compactUploadId,
        imageUrl: compactMedia,
      })
    }

    if (mediumMedia && mediumUploadId) {
      variants.push({
        type: 'medium_rectangle',
        uploadId: mediumUploadId,
        imageUrl: mediumMedia,
      })
    }

    const updatedPayload = {
      ...payload,
      variants,
    }

    updateCampaignFormatAction(updatedPayload, formatId, closeModal, setLoading)
  }

  const handleClickUploadInput = uploadRef => {
    return uploadRef?.current?.click()
  }

  const addImage = (variant, setMedia, setUploadId) => event => {
    const image = event?.target?.files?.[0]
    const imageSize = event.target?.files?.[0]?.size
    if (imageSize > 10000000) {
      return toast.error(t('imageTooBig', { limit: '10mb' }))
    }
    if (image) {
      const setLoadingState = loading => {
        setLoadingUpload(prev => ({ ...prev, [variant]: loading }))
      }
      uploadMediaToS3WithId(image, setLoadingState, setMedia, setUploadId)
    }
  }

  const addBillboardImage = addImage('billboard', setBillboardMedia, setBillboardUploadId)
  const addCompactImage = addImage('compact', setCompactMedia, setCompactUploadId)
  const addMediumImage = addImage('medium', setMediumMedia, setMediumUploadId)

  const variants = [
    {
      key: 'billboard',
      titleKey: 'billboardImage',
      helperKey: 'billboardSafeArea',
      loading: loadingUpload.billboard,
      media: billboardMedia,
      setMedia: setBillboardMedia,
      setUploadId: setBillboardUploadId,
      inputRef: billboardImageInput,
      onChange: addBillboardImage,
    },
    {
      key: 'compact',
      titleKey: 'compactRectangle',
      helperKey: 'compactRectangleSafeArea',
      loading: loadingUpload.compact,
      media: compactMedia,
      setMedia: setCompactMedia,
      setUploadId: setCompactUploadId,
      inputRef: compactImageInput,
      onChange: addCompactImage,
    },
    {
      key: 'medium',
      titleKey: 'mediumRectangle',
      helperKey: 'mediumRectangleSafeArea',
      loading: loadingUpload.medium,
      media: mediumMedia,
      setMedia: setMediumMedia,
      setUploadId: setMediumUploadId,
      inputRef: mediumImageInput,
      onChange: addMediumImage,
    },
  ]

  // Helper function to load existing banner data
  const loadExistingBannerData = () => {
    if (formatToUpdate?.variants && formatToUpdate.variants.length > 0) {
      // New format with variants
      formatToUpdate.variants.forEach(variant => {
        switch (variant.type) {
          case 'billboard':
            setBillboardMedia(variant.imageUrl)
            setBillboardUploadId(variant.uploadId)
            break
          case 'compact_rectangle':
            setCompactMedia(variant.imageUrl)
            setCompactUploadId(variant.uploadId)
            break
          case 'medium_rectangle':
            setMediumMedia(variant.imageUrl)
            setMediumUploadId(variant.uploadId)
            break
          default:
            break
        }
      })
    } else if (formatToUpdate?.imageUrl) {
      // Legacy format with single image - default to billboard
      setBillboardMedia(formatToUpdate.imageUrl)
      // Note: Legacy formats might not have uploadId, so we'll leave it null
    }
  }

  useEffect(() => {
    loadExistingBannerData()
    setDate(new Date(formatToUpdate?.date))
    setEndDate(new Date(formatToUpdate?.endDate))
  }, [showModal])

  const renderUploadSection = ({ titleKey, helperKey, loading, media, setMedia, setUploadId, inputRef, onChange }) => (
    <>
      {!review && (
        <S.InputContainer>
          <InputTitle>{t(titleKey)}</InputTitle>
          <S.HelperText>{t(helperKey)}</S.HelperText>
        </S.InputContainer>
      )}

      {loading && (
        <S.SpinnerContainer>
          <S.CustomSpinner />
        </S.SpinnerContainer>
      )}

      {!loading && !media && !review && (
        <S.UploadSection>
          <S.UploadContainer spacing onClick={() => handleClickUploadInput(inputRef)}>
            <S.ImageUploadIcon />
            <S.UploadText>{t('imageUploadFormat')}</S.UploadText>
          </S.UploadContainer>
        </S.UploadSection>
      )}

      {!loading && media && !review && (
        <S.PreviewMediaContainer>
          <S.PreviewMedia src={media} />
          <S.CloseIcon
            onClick={() => {
              setMedia(null)
              setUploadId(null)
            }}
          />
        </S.PreviewMediaContainer>
      )}
      <S.InputAddImage ref={inputRef} onChange={onChange} onClick={handleImageInputClick} />
    </>
  )

  return (
    <>
      <ProductsModal
        action={onSubmit}
        createMessage={refuseTheCampaignAndSendMessageToChat}
        approveCampaignProduct={approveCampaignProduct}
        loading={loading}
        modalTitle={review ? t('reviseFormat', { format: t('banner') }) : t('editFormat', { format: t('banner') })}
        buttonText={review ? t('done') : t('update')}
        handleCloseModal={closeModal}
        showModal={showModal}
        review={review}
        sponsor={sponsor}
        loadingUpload={loadingUpload.billboard || loadingUpload.compact || loadingUpload.medium}
        leftColumn={
          <S.Form onSubmit={handleSubmit(onSubmit)}>
            <S.FormContainer>
              <InputTitle>{t('title')}</InputTitle>
              <Controller
                as={
                  <S.CustomTextField
                    placeholder={t('typeTitle')}
                    disabled={review || false}
                    inputProps={{
                      maxLength: 80,
                    }}
                  />
                }
                defaultValue={formatToUpdate?.title}
                rules={{ required: t('defaultValidation', { inputName: t('title') }) }}
                name="title"
                control={control}
              />
              <ErrorMessage errors={errors} name="title" />

              <S.InputContainer>
                <InputTitle>{t('link')}</InputTitle>
                <S.HelperText>{t('bannerRedirectClick')}</S.HelperText>
                <Controller
                  as={
                    <S.CustomTextField
                      placeholder={t('typeLink')}
                      inputProps={{
                        maxLength: 500,
                      }}
                      disabled={review || false}
                    />
                  }
                  defaultValue={formatToUpdate?.link}
                  rules={{ required: t('defaultValidation', { inputName: t('link') }) }}
                  name="link"
                  control={control}
                />
                <ErrorMessage errors={errors} name="link" />
              </S.InputContainer>

              <StartDateSection
                product={t('banner')}
                selectedCampaignStartDate={date}
                setCampaignStartDate={setDate}
                campaignStartDate={currentCampaignForReviewPage.dtStart}
                campaignEndDate={currentCampaignForReviewPage.dtEnd}
                control={control}
                errors={errors}
                disabled={review || false}
              />

              <EndDateSection
                product={t('banner')}
                selectedCampaignStartDate={date}
                selectedCampaignEndDate={endDate}
                campaignEndDate={currentCampaignForReviewPage.dtEnd}
                setCampaignEndDate={setEndDate}
                control={control}
                errors={errors}
                disabled={review || false}
              />
            </S.FormContainer>

            {/* Upload Sections */}
            {variants.map(variant => (
              <React.Fragment key={variant.key}>{renderUploadSection(variant)}</React.Fragment>
            ))}
          </S.Form>
        }
        rightColumn={
          <SponsoredBannerPreview
            billboardMedia={billboardMedia}
            compactMedia={compactMedia}
            mediumMedia={mediumMedia}
          />
        }
      />
    </>
  )
}

export default ModalEditSponsoredBanner
